import type {SubtitleCueEvent, VTTProperties} from 'bitmovin-player';
import {CSSProperties} from 'react';

/**
 * Destructures out the container id for the subtitle cue. Various providers implement the structure
 * of the region identifier slightly differently, so this covers all use cases we've encountered.
 *
 * @param event the subtitle cue event
 * @return {string} the container id
 */

export function calculateContainerId(event: SubtitleCueEvent): string {
  if (event.vtt) {
    return event.vtt.region ? event.vtt.region.id : 'vtt';
  } else {
    return event.region || 'default';
  }
}

/**
 * Calculates a unique ID for a subtitle cue, which is needed to associate an CueEnter with its CueExit
 * event so we can remove the correct subtitle when multiple subtitles are active at the same time.
 * The start time plus the text should make a unique identifier, and in the only case where a collision
 * can happen, two similar texts will be displayed at a similar time and a similar position (or without position).
 * The start time should always be known, because it is required to schedule the CueEnter event. The end time
 * must not necessarily be known and therefore cannot be used for the ID.
 *
 * @param event the subtitle cue event to calculate id from
 * @return {string} the cue id
 */
export function calculateCueId(event: SubtitleCueEvent): string {
  let id = `${event.start}-${event.text}`;

  if (event.position) {
    id += `-${event.position.row}-${event.position.column}`;
  }

  return id;
}

export const lineHeight = 28;
export const defaultLineCount = 21;

/**
 * Calculates the relative positing in % of container height for the line number.
 * https://w3c.github.io/webvtt/#webvtt-cue-line
 *
 * @param vtt the cue's vtt object
 * @param containerHeight the height of the cue container in pixels
 * @returns the percentage position of the line
 */
function calculateRelativeLinePosition(
  vtt: VTTProperties,
  containerHeight: number
): string | undefined {
  const {line} = vtt;

  if (line === 'auto') return;

  let relativeLinePosition = typeof line === 'number' ? line : parseFloat(line);

  if (vtt.snapToLines) {
    let targetLine = Number(line);

    if (targetLine < 0) {
      targetLine = defaultLineCount + targetLine;
    }

    const lineHeight = containerHeight / defaultLineCount;
    const absoluteLinePosition = lineHeight * targetLine;

    relativeLinePosition = (100 * absoluteLinePosition) / containerHeight;
  }

  return `${relativeLinePosition}%`;
}

/**
 * Calcluates the alignment of the cue bos's line
 * https://w3c.github.io/webvtt/#webvtt-cue-line-alignment
 *
 * @param lineAlign The alignment of the line
 * @returns the alignment in pixels
 */
function calculateLineAlign(
  lineAlign: VTTProperties['lineAlign']
): string | undefined {
  if (lineAlign === 'center') {
    return `${-lineHeight / 2}px`;
  } else if (lineAlign === 'end') {
    return `${lineHeight}px`;
  } else {
    return undefined;
  }
}

/**
 * Calculates the css styles for the cue.
 * https://w3c.github.io/webvtt/#the-vttcue-interface
 *
 * @param vtt The cue's vtt object containing the configuration settings
 * @param container The container to render the cue within
 * @returns The CSS styles for the cue
 */
export function calculateCueBoxStyle(
  vtt: VTTProperties,
  container: HTMLDivElement
): CSSProperties {
  const {align, region, vertical, lineAlign, size} = vtt;

  const hasRegion = !!region;

  const styles: CSSProperties = hasRegion
    ? {
        position: 'relative',
        unicodeBidi: 'plaintext',
        display: 'inline-flex',
      }
    : {
        position: 'absolute',
        overflowWrap: 'break-word',
        overflow: 'hidden',
        flexFlow: 'column',
        display: 'inline-flex',
        unicodeBidi: 'plaintext',
      };

  // Set the writing direction of the subtitles
  const realtiveLinePosition = calculateRelativeLinePosition(
    vtt,
    container.getBoundingClientRect().height
  );

  if (vertical === '') {
    styles.writingMode = 'horizontal-tb';
    styles.bottom = 0;
    styles.top = realtiveLinePosition;
    styles.marginTop = calculateLineAlign(lineAlign);
  } else if (vertical === 'lr') {
    styles.writingMode = 'vertical-lr';
    styles.right = 0;
    styles.top = 0;
    styles.right = realtiveLinePosition;
    styles.marginRight = calculateLineAlign(lineAlign);
  } else if (vertical === 'rl') {
    styles.writingMode = 'vertical-rl';
    styles.left = 0;
    styles.top = 0;
    styles.left = realtiveLinePosition;
    styles.marginLeft = calculateLineAlign(lineAlign);
  }

  if (vertical === '') {
    styles.width = `${size}%`;
  } else {
    styles.height = `${size}%`;
  }

  // Some services use a out-of-spec "middle" value for `align`.
  styles.textAlign = align === 'middle' ? 'center' : align;

  return styles;
}

/**
 * Calculates the CSS styles from the VTT object.
 *
 * Additional Documentation:
 * https://www.w3.org/TR/webvtt1/#regions
 * https://www.speechpad.com/captions/webvtt#toc_16
 * @param event the subtitle cue event
 * @return
 */
export function calculateVttRegionStyles(
  vtt: VTTProperties,
  container: HTMLDivElement | null
): CSSProperties {
  const {region} = vtt;
  if (!container || !region) return {};

  const overlaySize = container.getBoundingClientRect();
  const regionX =
    (overlaySize.width * region.viewportAnchorX) / 100 -
    (((overlaySize.width * region.width) / 100) * region.regionAnchorX) / 100;
  const regionY =
    (overlaySize.height * region.viewportAnchorY) / 100 -
    (region.lines * lineHeight * region.regionAnchorY) / 100;

  return {
    position: 'absolute',
    overflow: 'hidden',
    width: `${region.width}%`,
    height: `${region.lines * lineHeight}px`,
    left: `${regionX}px`,
    right: 'unset',
    top: `${regionY}px`,
    bottom: 'unset',
  };
}
